/**
 * proFeaturesBindings.js — Привязка ВСЕХ функций viewport.js к UI
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Заменяет заглушки (showToast-only) на реальные вызовы через callAPI().
 * Инжектирует кнопки для функций, у которых нет HTML-элементов.
 *
 * Инициализировать из main.js ПОСЛЕ featureBindings (перезаписывает placeholder'ы).
 */

import { callAPI } from '../core/viewerAPI.js';
import { on as busOn, EVENTS } from '../core/eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { cornerstone } from '../core/init.js';
import { datasetManager } from '../managers/DatasetManager.js';
import { memoryGuardian } from '../managers/MemoryGuardian.js';
import { getAllSeries, getSeriesImageIds } from '../loaders/loaderManager.js'; // [7.0.52/53] 4D-авто-инициализация + выбор CT/PET серий

// Ленивая загрузка AI модуля (ONNX Runtime тяжёлый — не грузим при старте)
let _aiModule = null;
async function _ai() {
  if (_aiModule) return _aiModule;
  _aiModule = await import('../ai/aiModule.js');
  return _aiModule;
}
import { getRenderingEngine, getViewportIds } from '../core/viewportState.js';
import { popCameraState, getCameraHistoryLength, disablePositionSync, disableHUProbe, disableAdaptiveQuality, stopCine, getCineState, setCineSpeed, loadAnnotations, deleteAnnotation, clearAnnotations, exportAnnotationsJSON, importAnnotationsJSON, enableAnnotationAutoSave, removeVolumeOverlay, setOverlayOpacity, addHangingProtocol, listHangingProtocols, disableVoiceControl, addVoiceCommand, listVoiceCommands, computeRectROIStats, stopSessionRecording, replaySession, exportSessionJSON, disableGestures, enableAllGestures, stopSmartCompare, disableKeyboardShortcuts, addKeyboardShortcut, listKeyboardShortcuts, applyWLPreset, autoWindowLevel, goToBookmark, removeBookmark, getBookmarks, navigateBookmarks, enableMagnifier, disableMagnifier, showRulerGrid, removeRulerGrid, hideInfoOverlay, getSlabState, cycleSlabProjection, disableMPRSync, getVolumeClipping, addBookmark, startCine, showInfoOverlay, setSlabProjection, pushCameraState, startSessionRecording, init4DViewer, enablePositionSync, enableMPRSync, enableAdaptiveQuality, jumpToPathology } from '../core/viewport.js';
import { setCardiacPhase, nextCardiacPhase, prevCardiacPhase, start4DPlayback, stop4DPlayback, get4DState, detectCardiacPhases } from '../core/viewport4D.js';
import { downloadScreenshot, screenshotAll, downloadDICOMCapture } from '../core/viewportScreenshot.js';
import { animatePresetTransition } from '../core/viewportEffects.js';
import { окнаСЛупой, окнаСоСеткой, окнаСИнфо } from '../core/viewportUI.js';   // [12.0.57] переключатели знают, где включено
import { окноВыбранное } from './specModes.js';                                // [12.0.57] выбранное окно, а не первое
// [11.0.2] Окно справки открывается через общего распорядителя окон.
import { openModal } from './modalManager.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


const $ = (id) => document.getElementById(id);

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

function _getActiveVpId() {
  const ids = getViewportIds();
  return ids?.[0] || null;
}

/**
 * [12.0.57] ОКНО ДЛЯ КНОПОК ВКЛАДКИ «СПЕЦ. РЕЖИМЫ» — ВЫБРАННОЕ, А НЕ ПЕРВОЕ.
 * Все кнопки вкладки брали _getActiveVpId() — то есть первое окно раскладки.
 * В «MPR 6» это стопка «Нативные срезы»: на стенде лупа, сетка, «Инфо»,
 * закладки и камера уходили в стопку, когда врач работал в аксиальном
 * реформате, а слэб MIP «включался» на стопке, где его не бывает (с тостом
 * «🎬 MIP 10mm»). Теперь — окно, выбранное щелчком (тот же источник, что у
 * окна W/L с 12.0.49), с отказом словами, если выбрано не то.
 * @param {boolean} плоское — нужно окно со срезом (не объёмное 3D)
 */
function _окно(плоское = true) {
  const о = окноВыбранное({ плоское });
  if (!о.ok) { showToast(о.причина, 'warning', 3500); return null; }
  return о.id;
}

/**
 * Заменяет все обработчики кнопки (clone + replace)
 */
function _rebind(id, handler) {
  const el = $(id);
  if (!el) return null;
  const newEl = el.cloneNode(true);
  el.parentNode.replaceChild(newEl, el);
  newEl.addEventListener('click', handler, { signal: _ac.signal });
  return newEl;
}

// ══════════════════════════════════════════════════════════════
//  BIND: Закладки (Bookmarks) — заменяем заглушки
// ══════════════════════════════════════════════════════════════

function bindBookmarks() {
  // [12.0.57] Окно — выбранное (см. _окно); тост один — его говорит
  // addBookmark (прежде два на нажатие: «📌 Срез #32» и «⭐ Закладка #1»).
  _rebind('btn-bookmark-add', () => {
    const vpId = _окно();
    if (!vpId) return;
    if (!addBookmark(vpId, '', true)) showToast('Закладка не поставлена: окно ещё без снимка', 'warning', 3000);
  });

  _rebind('btn-bookmark-prev', () => {
    const vpId = _окно();
    if (vpId) navigateBookmarks(vpId, 'prev');
  });

  _rebind('btn-bookmark-next', () => {
    const vpId = _окно();
    if (vpId) navigateBookmarks(vpId, 'next');
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: CINE (Авто-прокрутка) — заменяем старые на viewport.js
// ══════════════════════════════════════════════════════════════

// [10.19.32] Четыре сироты панели наборов удалены: btn-ds-load-4d-cine,
// btn-ds-load-wholebody, btn-ds-play-cine, btn-ds-stop-cine — кнопок нет в
// разметке (панель наборов сведена к слиянию PET-CT). Живое 4D — кнопки
// btn-pro-4d-* и вкладка фаз (cardiacPhases, cardiac4d_test).

function bindCine() {
  // [10.19.32] Вкладочная пара кино (btn-cine-play/stop-tab) удалена: кнопок
  // нет с 10.19.19/26 — «кино в одном разделе». Живое кино: нижняя панель
  // (cine-play здесь же ниже) и кнопка тулбара btn-cine-toolbar.
  // Sidebar CINE play/stop


  // [10.19.32] Блок cine-fps-tab удалён вместе с элементом.

  // Bottom cine bar play button
  _rebind('cine-play', () => {
    const state = getCineState();
    if (state?.playing) {
      stopCine();
    } else {
      const vpId = _getActiveVpId();
      if (vpId) {
        const fpsEl = $('cine-fps');
        const fps = fpsEl ? parseInt(fpsEl.value) || 12 : 12;
        startCine(vpId, { fps, direction: 'forward', loop: !!$('cine-loop')?.checked });
      }
    }
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: DICOM Secondary Capture — заменяем заглушку
// ══════════════════════════════════════════════════════════════

// [10.19.32] bindDICOMSC удалён целиком: его единственная кнопка btn-export-sc
// давно вырезана из разметки. Живой DICOM SC — btn-pro-dicom-capture ниже
// (тот же downloadDICOMCapture).

// ══════════════════════════════════════════════════════════════
//  BIND: Инжектированные кнопки → viewport.js функции
// ══════════════════════════════════════════════════════════════

function bindProButtons() {
  const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn, { signal: _ac.signal }); };

  // ── Лупа / Сетка / Инфо ──
  // [12.0.57] Переключатели смотрят, где включено НА САМОМ ДЕЛЕ (у ядра), а не
  // в своём флажке: прежний флажок путался, стоило сменить окно — второе
  // нажатие «выключало» лупу в окне, где её не было. Тост — один на нажатие.
  on('btn-pro-magnifier', () => {
    const есть = окнаСЛупой();
    if (есть.length) {
      for (const id of есть) disableMagnifier(id);
      $('btn-pro-magnifier')?.classList.remove('active');
      showToast('Лупа выключена', 'info', 1000);
      return;
    }
    const vpId = _окно();
    if (!vpId) return;
    enableMagnifier(vpId);
    $('btn-pro-magnifier')?.classList.add('active');
    showToast(`Лупа ×3 в окне «${vpId}»: ведите мышью — под кругом число снимка в точке перекрестия`, 'info', 3000);
  });

  on('btn-pro-ruler-grid', () => {
    const есть = окнаСоСеткой();
    if (есть.length) {
      for (const id of есть) removeRulerGrid(id);
      $('btn-pro-ruler-grid')?.classList.remove('active');
      showToast('Сетка выключена', 'info', 1000);
      return;
    }
    const vpId = _окно();
    if (!vpId) return;
    const р = showRulerGrid(vpId);
    if (р && р.ok === false) { showToast(`Сетка не поставлена: ${р.причина}`, 'warning', 4000); return; }
    $('btn-pro-ruler-grid')?.classList.add('active');
    showToast(`Сетка в окне «${vpId}»: клетка 10 мм, полоса 50 мм — следует за увеличением`, 'info', 2500);
  });

  // «Инфо» — подписи во ВСЕХ окнах со срезом: этим кнопка и отличается от
  // общей подписи в углу экрана, которая показывает только выбранное окно.
  on('btn-pro-info-overlay', () => {
    const есть = окнаСИнфо();
    if (есть.length) {
      for (const id of есть) hideInfoOverlay(id);
      $('btn-pro-info-overlay')?.classList.remove('active');
      showToast('Подписи в окнах выключены', 'info', 1000);
      return;
    }
    const re = getRenderingEngine();
    const окна = getViewportIds().filter((id) => { const t = re?.getViewport(id)?.type; return t && t !== 'volume3d'; });
    if (!окна.length) { showToast('Нет окон со срезом — откройте исследование', 'warning', 3000); return; }
    for (const id of окна) showInfoOverlay(id);
    $('btn-pro-info-overlay')?.classList.add('active');
    showToast(`Подписи в окнах (${окна.length}): серия, срез, окно W/L, масштаб`, 'info', 2000);
  });

  // ── Slab Projection ──
  // [12.0.57] Слэб — в выбранном окне реформата; отказ и итог говорит ядро
  // (setSlabProjection): прежде кнопка печатала «🎬 MIP 10mm» при любом
  // исходе, а «цикл» — «Слэб: off» всегда (спрашивал состояние без окна).
  const слэб = (режим) => {
    const vpId = _окно();
    if (!vpId) return;
    const р = режим === 'цикл' ? cycleSlabProjection(vpId, 10) : setSlabProjection(vpId, режим, 10);
    if (р && р.ok === false) showToast(`Слэб не включён: ${р.причина}`, 'warning', 5000);
  };
  on('btn-pro-slab-mip', () => слэб('mip'));
  on('btn-pro-slab-minip', () => слэб('minip'));
  on('btn-pro-slab-avg', () => слэб('avgip'));
  on('btn-pro-slab-cycle', () => слэб('цикл'));

  // ── Screenshots ──
  on('btn-pro-screenshot-all', async () => {
    try {
      // [7.0.52 FIX] screenshotAll возвращает data-URL коллажа (строку) или null, НЕ массив.
      // Раньше: format:'image/png' → внутри 'image/image/png', а blobs.length = длина строки
      // (тост «287342 скриншотов»), и коллаж НИКУДА не сохранялся.
      const dataUrl = await screenshotAll({ format: 'png' });
      if (!dataUrl) { showToast('⚠️ Нет вьюпортов для скриншота', 'warning'); return; }
      const a = document.createElement('a');
      a.href = dataUrl; a.download = `screenshot_all_${Date.now()}.png`; a.click();
      showToast('📷 Коллаж-скриншот сохранён', 'success', 1500);
    } catch (e) { showToast('⚠️ ' + e.message, 'error'); }
  });
  // [12.0.57] Окно — выбранное; тост один — его говорит downloadDICOMCapture
  // (прежде «💾 DICOM SC сохранён» печатался и после «❌ Ошибка DICOM»).
  on('btn-pro-dicom-capture', () => {
    const vpId = _окно(false);
    if (!vpId) return;
    try { downloadDICOMCapture(vpId, `SC_${Date.now()}.dcm`); }
    catch (e) { showToast('DICOM SC не сохранён: ' + e.message, 'error', 4000); }
  });

  // ── Camera push / pop ──
  // [12.0.57] Камера — выбранного окна (на стенде запоминалась и
  // возвращалась камера стопки, пока врач увеличивал аксиальное окно).
  on('btn-pro-cam-push', () => {
    const vpId = _окно(false);
    if (vpId) { pushCameraState(vpId); showToast(`Вид окна «${vpId}» запомнен (в запасе: ${getCameraHistoryLength(vpId)})`, 'success', 1500); }
  });
  on('btn-pro-cam-pop', () => {
    const vpId = _окно(false);
    if (vpId) {
      const len = getCameraHistoryLength(vpId);
      if (len > 0) { popCameraState(vpId); showToast(`Вид окна «${vpId}» возвращён (в запасе: ${len - 1})`, 'info', 1500); }
      else showToast(`Для окна «${vpId}» запомненного вида нет — сначала «Запомнить»`, 'info', 2500);
    }
  });

  // ── Smart Compare ──
  // [12.0.57] «Сравнить» обещал «функция активируется автоматически» — не
  // активировалась никогда: startSmartCompare не зовёт никто, а флажок
  // _comparing не поднимался ни разу. Настоящее сравнение двух исследований
  // в программе есть — кнопка «Сравнение» верхней панели (comparePanel: два
  // окна, у каждого свой каталог, листание связано по анатомическому
  // уровню). Кнопка вкладки теперь нажимает её: путь один.
  on('btn-pro-smart-compare', () => {
    const главная = $('btn-compare-mode');
    if (главная) { главная.click(); return; }
    showToast('Сравнение двух исследований — кнопка «Сравнение» в верхней панели', 'info', 3500);
  });

  // ── Session Recording ──
  // [12.0.57] Запись была в одну сторону: «Записать все действия» писала
  // снимки камеры каждые полсекунды, а повторить записанное было нечем — JSON
  // уходил в файл, который программа не читает; снимков экрана в нём не было
  // (клались обещания вместо картинок). Теперь: «Повтор» проигрывает
  // последнюю запись в окнах (камера, кадр стопки, окно W/L), а «Экспорт»
  // отдаёт её с конечными снимками окон. Подпись кнопки говорит, что пишется.
  let _sessionData = null;
  let _повтор = null;
  const _записьИдёт = () => $('btn-pro-session-start')?.classList.contains('active');
  on('btn-pro-session-start', () => {
    if (_записьИдёт()) { showToast('Запись уже идёт — «Стоп», чтобы закончить', 'info', 2000); return; }
    startSessionRecording({ source: 'ui' });
    $('btn-pro-session-start')?.classList.add('active');
    showToast('Запись пошла: листание, масштаб, повороты и окно W/L во всех окнах. «Стоп» — закончить', 'info', 3000);
  });
  on('btn-pro-session-stop', () => {
    if (!_записьИдёт()) { showToast('Запись не идёт — сначала «Запись»', 'info', 2000); return; }
    _sessionData = stopSessionRecording();
    $('btn-pro-session-start')?.classList.remove('active');
    const видов = (_sessionData?.events || []).filter((е) => е.type === 'CAMERA_SNAPSHOT').length;
    if (_sessionData) showToast(`Записано ${((_sessionData.duration || 0) / 1000).toFixed(0)} с: смен вида — ${видов}. «Повтор» — проиграть, «Экспорт» — в файл`, 'success', 3500);
    else showToast('Записи нет', 'info', 1500);
  });
  on('btn-pro-session-replay', () => {
    if (_повтор) { _повтор.stop(); _повтор = null; $('btn-pro-session-replay')?.classList.remove('active'); showToast('Повтор остановлен', 'info', 1200); return; }
    if (!_sessionData?.events?.length) { showToast('Повторять нечего — сначала «Запись», потом «Стоп»', 'warning', 3000); return; }
    _повтор = replaySession(_sessionData, { speed: 1 });
    $('btn-pro-session-replay')?.classList.add('active');
    const мс = (_sessionData.duration || 0) + 600;
    setTimeout(() => { if (_повтор) { _повтор = null; $('btn-pro-session-replay')?.classList.remove('active'); } }, мс);
  });
  on('btn-pro-session-export', () => {
    if (!_sessionData) { showToast('Выгружать нечего — сначала «Запись», потом «Стоп»', 'warning', 3000); return; }
    const json = exportSessionJSON(_sessionData);
    if (json) {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_${Date.now()}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      showToast(`Запись выгружена (${(blob.size / 1024).toFixed(0)} КБ, со снимками окон)`, 'success', 2000);
    }
  });

  // ── Keyboard shortcuts list ──
  on('btn-pro-kb-list', () => {
    // [11.0.2] Раньше здесь список из тридцати строк вываливался ВСПЛЫВАЮЩЕЙ
    // ПЛАШКОЙ на 8 секунд. Прочитать её нельзя, прокрутить нельзя, скопировать
    // нельзя — та самая беда, из-за которой в 10.19.29 проверку моделей увели
    // из плашки в окно, а в 10.19.44 туда же ушла диагностика.
    //
    // Теперь кнопка открывает окно «Горячие клавиши» — то самое, где таблица
    // клавиш лежит целиком и куда при роспуске панели переехала группа
    // «Горячие клавиши / Шпаргалка». Двух списков в двух видах больше нет:
    // кнопка и окно ведут в одно место.
    // [11.0.2] Через openModal, а не classList.add('active'): окно ведёт
    // ModalManager — ловушка фокуса, Escape, клик по подложке. Самодельное
    // открытие запрещено сторожем (uiwiring_test, «окна открываются только
    // через ModalManager») — и правильно: самоделка открывает ничто, молча.
    try { openModal('modal-help'); }
    catch (e) { log.debug('[Клавиши] окно справки:', e.message); }
  });

  // ── Auto W/L ──
  // [12.0.57] От выбранного окна; итог говорит autoWindowLevel (прежде
  // перед ним шёл пустой «🎯 Авто W/L» — даже когда кадр не загружен).
  on('btn-pro-auto-wl', () => {
    const vpId = _окно();
    if (vpId) autoWindowLevel(vpId);
  });

  // ── 4D ──
  on('btn-pro-4d-prev', () => prevCardiacPhase());
  on('btn-pro-4d-next', () => nextCardiacPhase());
  let _4dPlaying = false;
  on('btn-pro-4d-play', () => {
    if (_4dPlaying) { stop4DPlayback(); _4dPlaying = false; $('btn-pro-4d-play')?.classList.remove('active'); showToast('⏹ 4D Стоп', 'info', 800); return; }
    // [7.0.52 FIX] Раньше кнопка ЛГАЛА: init4DViewer нигде не вызывался → _4dState=null →
    // start4DPlayback молча no-op, но кнопка вставала в active и тост «▶ 4D Play». Теперь честно:
    // если 4D не инициализирован — пытаемся инициализировать из многофазных серий, а если их
    // нет (нет тегов фазы) — прямо говорим, а не притворяемся.
    if (!get4DState()) {
      const phases = detectCardiacPhases(getAllSeries?.() || []);
      const hasPhaseInfo = phases.length >= 2 && phases.some(p => p.phasePercent > 0);
      if (!hasPhaseInfo) { showToast('⚠️ 4D: нужна многофазная кардио-серия (теги фазы 0018,1060 / 0020,9241)', 'warning', 4000); return; }
      init4DViewer(phases, getViewportIds());
      if (!get4DState()) { showToast('⚠️ 4D: не удалось инициализировать', 'warning', 3000); return; }
      showToast(`🫀 4D: ${phases.length} фаз обнаружено`, 'info', 1500);
    }
    start4DPlayback({ fps: 8, loop: true });
    _4dPlaying = true; $('btn-pro-4d-play')?.classList.add('active'); showToast('▶ 4D Пуск', 'info', 800);
  });
  on('btn-pro-4d-stop', () => {
    stop4DPlayback();
    _4dPlaying = false;
    $('btn-pro-4d-play')?.classList.remove('active');
    showToast('⏹ 4D Стоп', 'info', 800);
  });

  // ── Pos sync / MPR sync toggles ──
  let _posSyncOn = false;
  on('btn-pro-pos-sync', () => {
    _posSyncOn = !_posSyncOn;
    if (_posSyncOn) {
      const ids = getViewportIds();
      enablePositionSync(ids);
      showToast('🔗 Позиционная синхр. ВКЛ', 'info', 1000);
    } else {
      disablePositionSync();
      showToast('🔗 Позиционная синхр. ВЫКЛ', 'info', 1000);
    }
    $('btn-pro-pos-sync')?.classList.toggle('active', _posSyncOn);
  });

  let _mprSyncOn = false;
  on('btn-pro-mpr-sync', () => {
    _mprSyncOn = !_mprSyncOn;
    if (_mprSyncOn) { enableMPRSync(); showToast('🔗 MPR W/L синхр. ВКЛ', 'info', 1000); }
    else { disableMPRSync(); showToast('🔗 MPR W/L синхр. ВЫКЛ', 'info', 1000); }
    $('btn-pro-mpr-sync')?.classList.toggle('active', _mprSyncOn);
  });

  // ── ROI Stats ──
  on('btn-pro-roi-circle', () => {
    showToast('⭕ Нарисуйте круговую область инструментом, статистика отобразится автоматически', 'info', 3000);
  });
  on('btn-pro-roi-rect', () => {
    showToast('▬ Нарисуйте прямоугольную область инструментом, статистика отобразится автоматически', 'info', 3000);
  });

  // ── Adaptive quality ──
  on('btn-pro-adaptive-on', () => {
    const vpId = _getActiveVpId();
    if (vpId) { enableAdaptiveQuality(vpId); showToast('⚡ Адаптивное качество ВКЛ', 'info', 1000); }
    $('btn-pro-adaptive-on')?.classList.add('active');
    $('btn-pro-adaptive-off')?.classList.remove('active');
  });
  on('btn-pro-adaptive-off', () => {
    const vpId = _getActiveVpId();
    if (vpId) { disableAdaptiveQuality(vpId); showToast('⚡ Адаптивное качество ВЫКЛ', 'info', 1000); }
    $('btn-pro-adaptive-on')?.classList.remove('active');
  });

  // ── AI pathology jump ──
  // [12.0.56] «К патологии» (btn-pro-jump-pathology) УДАЛЕНА вместе с карточкой. Она
  // не шла к «последней находке ИИ», как обещала подпись: jumpToPathology ищет срез,
  // где больше всего вокселей ≥130 HU, — это срез с наибольшей костью, — и пишет
  // «🎯 Патология: срез #…». Та же эвристика «по яркости без понимания органа», что
  // убрала «Найти патологию» в 10.19.31.
}

// ══════════════════════════════════════════════════════════════
//  BIND: EventBus listeners для информирования пользователя
// ══════════════════════════════════════════════════════════════

function bindEventListeners() {
  // HU Probe → обновляем statusbar (desktop + mobile)
  busOn('hu:probe', (data) => {
    const val = data?.hu != null ? `HU: ${Math.round(data.hu)}` : 'HU: —';
    const el = $('hu-value-display');
    if (el) el.textContent = val;
    const elM = $('hu-value-display-m');
    if (elM) elM.textContent = val;
  });

  // CINE events → обновляем UI кнопок
  busOn('cine:start', () => {
    $('btn-cine-play-tab')?.classList.add('active');
    $('cine-play')?.classList.add('active');
  });
  busOn('cine:stop', () => {
    $('btn-cine-play-tab')?.classList.remove('active');
    $('cine-play')?.classList.remove('active');
  });

  // Keyboard shortcuts ready
  busOn('keyboard:shortcut', (data) => {
    log.debug(`[KB] ${data?.code}: ${data?.label}`);
  });

  // 4D phase changes
  busOn('phase:changed', (data) => {
    const el = $('4d-phase-display');
    if (el) el.textContent = `Фаза: ${(data?.phase ?? 0) + 1}/${data?.totalPhases || '?'}`;
  });

  // Auto W/L applied
  busOn('auto:wl:applied', (data) => {
    if (data?.preset) showToast(`🎯 Авто W/L: ${data.preset}`, 'info', 1200);
  });
}

// ══════════════════════════════════════════════════════════════
//  BIND: Enterprise Dataset Manager (4D Cine, PET-CT, Whole Body)
// ══════════════════════════════════════════════════════════════

/** Текущий активный датасет (для UI-кнопок play/stop/destroy) */
let _activeDatasetId = null;

function bindDatasetManager() {
  const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn, { signal: _ac.signal }); };

  // ── Прогресс-бар и статус (общие для всех типов) ──
  const progressBar = $('ds-progress-bar');
  const progressText = $('ds-progress-text');
  const statusText = $('ds-status');

  datasetManager.addEventListener('progress', (e) => {
    const { id, percent } = e.detail;
    if (progressBar) { progressBar.style.width = `${percent}%`; progressBar.textContent = `${percent}%`; }
    if (progressText) progressText.textContent = `${percent}%`;
  }, { signal: _ac.signal });

  datasetManager.addEventListener('loaded', (e) => {
    if (progressBar) { progressBar.style.width = '100%'; progressBar.textContent = '100%'; }
    if (statusText) statusText.textContent = `✓ Загружен: ${e.detail.id}`;
    showToast(`✅ Датасет загружен: ${e.detail.id}`, 'success', 2000);
    log.info(`[DS] Fully loaded: ${e.detail.id}`);
  }, { signal: _ac.signal });

  datasetManager.addEventListener('destroyed', (e) => {
    if (statusText) statusText.textContent = '';
    if (progressBar) { progressBar.style.width = '0%'; progressBar.textContent = ''; }
    showToast(`🗑️ Датасет удалён: ${e.detail.id}`, 'info', 1500);
    if (_activeDatasetId === e.detail.id) _activeDatasetId = null;
  }, { signal: _ac.signal });

  datasetManager.addEventListener('cine-frame', (e) => {
    const el = $('ds-cine-frame-display');
    if (el) el.textContent = `Кадр: ${e.detail.frame + 1}`;
  }, { signal: _ac.signal });

  datasetManager.addEventListener('cine-play', (e) => {
    // [10.19.32] Подсветка btn-ds-play-cine убрана вместе с кнопкой-сиротой.
    $('btn-ds-stop-cine')?.classList.toggle('active', !e.detail.playing);
  }, { signal: _ac.signal });

  // ── 4D CINE ──



  // FPS slider для DatasetManager CINE
  const dsFpsSlider = $('ds-cine-fps');
  if (dsFpsSlider) {
    const dsFpsVal = $('ds-cine-fps-val');
    dsFpsSlider.addEventListener('input', () => {
      const fps = parseInt(dsFpsSlider.value) || 25;
      if (dsFpsVal) dsFpsVal.textContent = fps;
      if (_activeDatasetId) {
        const vpId = _getActiveVpId();
        const re = getRenderingEngine();
        const vp = vpId ? re?.getViewport(vpId) : null;
        datasetManager.setCineFPS(_activeDatasetId, fps, vp);
      }
    }, { signal: _ac.signal });
  }

  // Timepoint slider (ручной скроллинг по фазам)
  const dsTimepointSlider = $('ds-cine-timepoint');
  if (dsTimepointSlider) {
    dsTimepointSlider.addEventListener('input', () => {
      if (!_activeDatasetId) return;
      const t = parseInt(dsTimepointSlider.value) || 0;
      const vpId = _getActiveVpId();
      const re = getRenderingEngine();
      const vp = vpId ? re?.getViewport(vpId) : null;
      datasetManager.setCineTimepoint(_activeDatasetId, t, vp);
    }, { signal: _ac.signal });
  }

  // ── [7.0.53] Выбор CT/PET серий → window vars, которые нужны loadPETCTFusion ──
  const _esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
  function _refreshFusionSeries(silent) {
    const ctSel = $('ds-petct-ct-series'), petSel = $('ds-petct-pet-series');
    if (!ctSel || !petSel) return;
    const series = getAllSeries?.() || [];
    const opts = '<option value="">—</option>' + series.map((s, i) => {
      const uid = s.seriesUID || s.seriesInstanceUID || '';
      const nm = s.seriesDescription || s.modality || `Серия ${i + 1}`;
      return `<option value="${_esc(uid)}">${_esc(nm)}${s.modality ? ' [' + _esc(s.modality) + ']' : ''}</option>`;
    }).join('');
    const ctv = ctSel.value, petv = petSel.value;         // сохранить выбор при перерисовке
    ctSel.innerHTML = opts; petSel.innerHTML = opts;
    ctSel.value = ctv; petSel.value = petv;
    if (!silent) showToast(`↻ Загруженных серий: ${series.length}`, 'info', 1200);
  }
  function _setFusionSeriesVars() {
    const ctUID = $('ds-petct-ct-series')?.value || '';
    const petUID = $('ds-petct-pet-series')?.value || '';
    window._mikhmelPETCT_ctIds = ctUID ? getSeriesImageIds(ctUID) : null;
    window._mikhmelPETCT_petIds = petUID ? getSeriesImageIds(petUID) : null;
  }
  on('btn-ds-refresh-series', () => _refreshFusionSeries(false));
  $('ds-petct-ct-series')?.addEventListener('change', _setFusionSeriesVars, { signal: _ac.signal });
  $('ds-petct-pet-series')?.addEventListener('change', _setFusionSeriesVars, { signal: _ac.signal });
  _refreshFusionSeries(true); // при старте серий ещё нет — заполнится по кнопке ↻

  // ── PET-CT Fusion ──
  on('btn-ds-load-petct', async () => {
    // PET-CT требует два набора imageIds — CT и PET
    // В реальном UI они задаются через выбор серий в sidebar
    const ctIds = window._mikhmelPETCT_ctIds;
    const petIds = window._mikhmelPETCT_petIds;

    if (!ctIds?.length || !petIds?.length) {
      showToast('⚠️ Выберите CT и PET серии для слияния\n(Загрузите оба набора через боковую панель)', 'warning', 4000);
      return;
    }

    try {
      showToast('⏳ Готовлю слияние PET-CT…', 'info', 3000);
      const ds = await datasetManager.loadPETCTFusion(ctIds, petIds);
      _activeDatasetId = ds.id;
      if (statusText) statusText.textContent = `Слияние PET-CT: ${ctIds.length} CT + ${petIds.length} PET`;
      showToast('🔥 Слияние PET-CT готово', 'success', 2000);
    } catch (e) {
      if (e.name !== 'AbortError') showToast('⚠️ Ошибка PET-CT: ' + e.message, 'error');
    }
  });

  on('btn-ds-apply-fusion', async () => {
    if (!_activeDatasetId) { showToast('⚠️ Сначала загрузите PET-CT', 'warning'); return; }
    const re = getRenderingEngine();
    const vpIds = getViewportIds();
    if (!re || !vpIds.length) return;

    try {
      await datasetManager.applyFusion(_activeDatasetId, re, vpIds);
      showToast('🔥 Слияние применено ко всем окнам', 'success', 1500);
    } catch (e) {
      showToast('⚠️ ' + e.message, 'error');
    }
  });

  // Fusion opacity slider
  const fusionOpacity = $('ds-fusion-opacity');
  if (fusionOpacity) {
    fusionOpacity.addEventListener('input', () => {
      if (!_activeDatasetId) return;
      const opacity = parseFloat(fusionOpacity.value) || 0.5;
      const re = getRenderingEngine();
      const vpIds = getViewportIds();
      datasetManager.updateFusionProperties(_activeDatasetId, re, vpIds, { opacity });
    }, { signal: _ac.signal });
  }

  // Fusion colormap select
  const fusionColormap = $('ds-fusion-colormap');
  if (fusionColormap) {
    fusionColormap.addEventListener('change', () => {
      if (!_activeDatasetId) return;
      const colormap = fusionColormap.value || 'hot';
      const re = getRenderingEngine();
      const vpIds = getViewportIds();
      datasetManager.updateFusionProperties(_activeDatasetId, re, vpIds, { colormap });
    }, { signal: _ac.signal });
  }

  // ── Whole Body MRI ──

  // Station navigation buttons
  const stationBtns = document.querySelectorAll('[data-wb-station]');
  stationBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (!_activeDatasetId) return;
      const idx = parseInt(btn.dataset.wbStation) || 0;
      const station = datasetManager.navigateToStation(_activeDatasetId, idx);
      if (station) showToast(`📍 Станция: ${station.label}`, 'info', 1000);
    }, { signal: _ac.signal });
  });

  // ── Общие кнопки управления ──
  on('btn-ds-destroy', () => {
    if (!_activeDatasetId) { showToast('⚠️ Нет активного датасета', 'warning'); return; }
    datasetManager.destroyDataset(_activeDatasetId);
  });

  on('btn-ds-clear-all', () => {
    datasetManager.clearAll();
    showToast('🗑️ Все датасеты удалены', 'info', 1500);
  });

  // ── Memory Guardian UI ──
  on('btn-memory-status', () => {
    if (!performance.memory) {
      showToast('ℹ️ API памяти недоступен в этом браузере', 'info', 2000);
      return;
    }
    const mem = performance.memory;
    const used = (mem.usedJSHeapSize / 1024 / 1024).toFixed(0);
    const limit = (mem.jsHeapSizeLimit / 1024 / 1024).toFixed(0);
    const ratio = ((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100).toFixed(1);
    const datasets = datasetManager.getAllDatasets();
    showToast(`🧠 RAM: ${used} / ${limit} MB (${ratio}%)\n` +
      `📦 Датасетов: ${datasets.length}\n` +
      `🛡️ Страж: ${memoryGuardian.hasMemoryAPI ? 'Active' : 'Passive'}`,
      'info', 5000
    );
  });

  log.debug('[ProFeatures] DatasetManager & MemoryGuardian wired to UI');
}

// ══════════════════════════════════════════════════════════════
//  HELPER: Извлечение данных тома из активного viewport
// ══════════════════════════════════════════════════════════════

/**
 * Получает scalarData, dims, spacing из загруженного тома.
 * Паттерн идентичен computeCalciumScore в viewport.js.
 * @returns {{ scalarData, dims, spacing, origin, direction, volumeId } | null}
 */
function _getActiveVolumeData() {
  const re = getRenderingEngine();
  const diag = []; // diagnostic log
  if (!re) { diag.push('RE=null'); log.warn('[AI-DIAG]', diag.join(' | ')); return null; }

  let volume = null;
  let volumeId = null;
  const cache = cornerstone.cache;
  diag.push(`cache=${!!cache}`, `cache.getVolume=${typeof cache?.getVolume}`);

  // 1. Ищем том через actors в viewport'ах
  const vpIds = getViewportIds();
  diag.push(`vpIds=[${vpIds.join(',')}]`);

  for (const vpId of vpIds) {
    const vp = re.getViewport(vpId);
    if (!vp) { diag.push(`vp(${vpId})=null`); continue; }
    diag.push(`vp(${vpId}).type=${vp.type}`);
    
    const actors = vp.getActors?.();
    diag.push(`actors=${actors?.length || 0}`);
    
    if (actors?.length) {
      const a0 = actors[0];
      const uid = a0.uid || a0.referencedId || a0.referenceId || a0.volumeId;
      diag.push(`actor0.keys=[${Object.keys(a0).join(',')}]`, `uid=${uid}`);
      
      if (uid) {
        // Пробуем несколько вариантов получения тома из кэша
        volume = cache?.getVolume?.(uid);
        if (!volume) volume = cache?.getVolume?.(`cornerstoneStreamingImageVolume:${uid}`);
        if (!volume) {
          // Пробуем без префикса если uid уже содержит его
          const cleanId = uid.replace('cornerstoneStreamingImageVolume:', '');
          volume = cache?.getVolume?.(`cornerstoneStreamingImageVolume:${cleanId}`);
        }
        volumeId = uid;
        diag.push(`volume=${!!volume}`);
        if (volume) break;
      }
    }

    // Для любого viewport — пробуем getImageData
    try {
      const imageData = vp.getImageData?.();
      if (imageData) {
        const scalarData = imageData.getPointData?.()?.getScalars?.()?.getData?.();
        const dims = imageData.getDimensions?.();
        const spacing = imageData.getSpacing?.();
        diag.push(`imageData: dims=${dims}, scalars=${scalarData?.length || 0}`);
        if (scalarData?.length && dims) {
          log.info('[AI-DIAG] Found via getImageData:', diag.join(' | '));
          // [10.19.19] origin и direction тоже наружу: без них точку экрана нельзя
          // перевести в индексы объёма (наращивание области), а origin у
          // медицинских серий — это сотни миллиметров со знаком, не ноль.
          const origin = imageData.getOrigin?.();
          const direction = imageData.getDirection?.();
          return {
            scalarData, dims, spacing: spacing || [1, 1, 1],
            origin: origin ? Array.from(origin) : [0, 0, 0],
            direction: direction && direction.length === 9 ? Array.from(direction) : null,
            volumeId: volumeId || 'imageData',
          };
        }
      }
    } catch (e) { diag.push(`imageData.err=${e.message}`); }
  }

  // 2. Fallback: перебираем весь кэш
  if (!volume && cache) {
    try {
      const volCache = cache._volumeCache || cache.getVolumes?.();
      diag.push(`volCache=${!!volCache}, type=${volCache?.constructor?.name}`);
      if (volCache) {
        const entries = volCache instanceof Map ? [...volCache.entries()] :
                        typeof volCache[Symbol.iterator] === 'function' ? [...volCache] :
                        Array.isArray(volCache) ? volCache.map((v,i) => [i, v]) :
                        Object.entries(volCache);
        diag.push(`volCache.entries=${entries.length}`);
        for (const [vid, entry] of entries) {
          const v = entry?.volume || entry;
          if (v?.dimensions) {
            volume = v;
            volumeId = vid;
            diag.push(`fallback.found=${vid}, dims=${v.dimensions}`);
            break;
          }
        }
      }
    } catch (e) { diag.push(`fallback.err=${e.message}`); }
  }

  log.warn('[AI-DIAG]', diag.join(' | '));
  if (!volume) return null;

  // 3. Извлекаем скалярные данные
  let scalarData = null;
  try { scalarData = volume.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); }
  if (!scalarData && volume.imageData) {
    try { scalarData = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); }
  }
  if (!scalarData) { try { scalarData = volume.getScalarData?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); } }
  if (!scalarData) { log.warn('[AI-DIAG] volume found but no scalarData'); return null; }

  const dims = volume.dimensions || volume.imageData?.getDimensions?.() || [512, 512, 1];
  const spacing = volume.spacing || volume.imageData?.getSpacing?.() || [1, 1, 1];

  // origin/direction нужны для перевода координат экрана в индексы объёма.
  let origin = null, direction = null;
  try {
    origin = volume.origin || volume.imageData?.getOrigin?.();
    direction = volume.direction || volume.imageData?.getDirection?.();
  } catch (e) { /* геометрии нет — вернём безопасные значения */ }
  return {
    scalarData, dims, spacing, volumeId,
    origin: origin ? Array.from(origin) : [0, 0, 0],
    direction: direction && direction.length === 9 ? Array.from(direction) : null,
  };
}

// ══════════════════════════════════════════════════════════════
//  BIND: AI Module (Все алгоритмы → UI кнопки)
// ══════════════════════════════════════════════════════════════

// [12.0.56] ВКЛАДКА ИИ: ИЗ ЧЕТЫРНАДЦАТИ ОБРАБОТЧИКОВ ЗДЕСЬ ОСТАЛСЯ ОДИН — «Выгрузить модели».
//
// Остальные удалены вместе с карточками (надгробие — index.html, вкладка ИИ).
// Проверено ПО ТОМУ, ЧТО КНОПКА ДЕЛАЛА, а не по наличию обработчика:
//   · «Расш. анализ лёгких» — брал organs.lungs.mask, которого нет: всегда «Лёгкие
//     не сегментированы»; а будь маска — «пневмония», «саркоидоз» по порогам HU;
//   · «Оппортунистический» — падал TypeError на поле аорты (строка toFixed) и
//     читал поля liverSpleen, smi, findings, которых функции не отдают;
//   · «Авто-контур» — «контур» = все воксели −100…200 HU тома, маски нет;
//   · «Region Growing» — двойник «Рост области 3D» («Клинические»), но маску клал
//     только в кинорендер, на срезах её не было; настоящий — там, он красит сегмент;
//   · «STL органа» — искал маску в ключах ответа ('organs', 'totalVoxels'…), а
//     сетку строила заглушка: «Нет сегментированных органов» всегда. STL органа —
//     вкладка «Сегментация» (btn-seg-organ-stl);
//   · «Область тела» — функция отдаёт строку, кнопка читала .bodyPart и
//     .confidence: «Область: Unknown (NaN%)»;
//   · «Calcium Score» — второй вход в «Индекс Агатстона» (вкладка «ЭхоКГ», там
//     все расчёты по сердцу — решение владельца 12.0.53);
//   · «SAM Вкл/Выкл» — второй выключатель MedSAM без своей кнопки «Запуск»; раздел
//     «Обводка сетью MedSAM» теперь стоит в этой же вкладке;
//   · «К патологии» — переход к срезу, где больше всего кости; привязка из
//     bindProButtons снята там же (надгробие [12.0.56]).
function bindAI() {
  const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn, { signal: _ac.signal }); };
  // «Выгрузить модели» — говорит, сколько выгружено: прежде «выгружены» печаталось и тогда,
  // когда не было загружено ни одной.
  on('btn-ai-release', async () => {
    try {
      const n = await (await _ai()).releaseModels();
      showToast(n ? `Выгружено моделей: ${n} — память освобождена. Следующий запуск загрузит их заново`
        : 'Ни одна модель сейчас не загружена — выгружать нечего', n ? 'success' : 'info', 3500);
    } catch (e) { showToast('Выгрузка моделей: ' + e.message, 'error', 5000); }
  });
  log.debug('[ProFeatures] вкладка ИИ: «Выгрузить модели» привязана');
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC: initProFeaturesBindings
// ══════════════════════════════════════════════════════════════

/**
 * Инициализация всех Pro-привязок.
 * Вызывать из main.js ПОСЛЕ initFeatureBindings и ПОСЛЕ viewport.js загружен.
 */
export function initProFeaturesBindings() {
  bindAIControls(); // [10.19.43]
  try {
    // 1. Заменяем заглушки реальными вызовами
    bindBookmarks();
    bindCine();
    // bindDICOMSC(); // [10.19.32] удалён вместе с кнопкой-сиротой

    // 2. Привязываем кнопки (уже в index.html) к viewport.js
    bindProButtons();

    // 3. Подписываемся на события для обновления UI
    bindEventListeners();

    // 4. Enterprise DatasetManager + MemoryGuardian UI
    bindDatasetManager();

    // 5. AI Module — все алгоритмы (lazy load при первом нажатии)
    bindAI();

    log.debug('[ProFeatures] All viewport.js + DatasetManager + AI wired to UI');
  } catch (e) {
    log.warn('[ProFeatures] Init error:', e.message);
  }
}

// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — AI config, batch export, overlay, scheduling
// ══════════════════════════════════════════════════════════════

const _aiResultHistory = [];   // [12.0.56] порог и показ масок удалены — см. ниже
const AI_HISTORY_MAX = 30;

/**
 * Set AI confidence threshold (filters results below this value)
 * @param {number} threshold — 0.0 to 1.0
 */
// [12.0.56] УДАЛЕНЫ: setAIConfidence/getAIConfidence — «Порог уверенности» не читал
// никто, ползунок двигал число в пустоту; toggleAIOverlays — прятал элементы
// классов .ai-overlay, .ai-segmentation-mask, [data-ai-result], которых в
// программе нет (маски — холст Cornerstone, их прячет «Прозрачность» в
// «Сегментации»); runAIBatchAndExport и scheduleAIOnLoad не звал никто, а
// первая гнала те самые пороговые «органы», «мозг» и «камеры сердца» в JSON.

/**
 * Get current AI confidence threshold
 * @returns {number}
 */

/**
 * Toggle AI result overlays visibility on viewports
 * @returns {boolean} new state
 */

/**
 * Run multiple AI analyses and export combined report
 * @param {string[]} analyses — e.g. ['calcium', 'cardiac', 'lung-nodules']
 * @returns {Promise<Object>}
 */

/**
 * Get AI analysis history
 * @returns {Array}
 */
export function getAIHistory() { return [..._aiResultHistory]; }

/**
 * Schedule AI analysis to run after volume load
 * @param {string[]} analyses — auto-run these after next volume load
 */

export function getProFeaturesState() {
  return { activeDatasetId: _activeDatasetId, aiLoaded: !!_aiModule, historySize: _aiResultHistory.length };
}
export function getActiveVolumeInfo() { const v = _getActiveVolumeData(); return v ? { dims: v.dims, spacing: v.spacing, voxels: v.scalarData?.length || 0 } : null; }
export function getActiveDatasetId() { return _activeDatasetId; }
export function getMemoryInfo() { const m = performance.memory; return m ? { usedMB: +(m.usedJSHeapSize/1048576).toFixed(0), limitMB: +(m.jsHeapSizeLimit/1048576).toFixed(0) } : null; }
/**
 * [12.0.56] Прогрев — с отчётом. Прежде «🤖 AI готов» и «✅ Модели готовы»
 * печатались и без рантайма, и без единого файла: ошибки прогрева глотались.
 * @returns {Promise<string>} что сказать врачу
 */
export async function preloadAIModule() {
  const р = await (await _ai()).warmupModels();
  if (!р?.рантайм) return 'Движок ONNX не поднялся — модели прогревать нечем (подробности в журнале)';
  const имя = { 'medsam-encoder': 'MedSAM', lungmask: 'лёгкие (lungmask)' };
  const ч = [];
  if (р.прогреты.length) ч.push('прогреты: ' + р.прогреты.map((м) => имя[м] || м).join(', '));
  if (р.нетФайла.length) ч.push('нет файла: ' + р.нетФайла.map((м) => имя[м] || м).join(', '));
  if (р.ошибки.length) ч.push('не прогрелись: ' + р.ошибки.join('; '));
  return (ч.join('; ') || 'прогревать нечего') + '. Печень и органы грузятся при запуске — заранее их не держим, они тяжёлые';
}
export async function releaseAIModels() { try { return await (await _ai()).releaseModels?.(); } catch (e) { log.debug("[proFeaturesBindings]", e.message); return 0; } }
export function destroyActiveDataset() {
  _ac.abort(); _ac = new AbortController(); if (_activeDatasetId) { datasetManager.destroyDataset(_activeDatasetId); _activeDatasetId = null; } }
export function clearAllDatasets() { datasetManager.clearAll(); _activeDatasetId = null; }


// [10.19.43] Управление ИИ — переезд из Enterprise (решение владельца, п.9).
// [12.0.56] Остался прогрев — с честным отчётом. «Порог уверенности» и «Показ
// масок ИИ» удалены: первый не читал никто, второй прятал несуществующие
// элементы (надгробие — у setAIConfidence выше).
export function bindAIControls() {
  const п = document.getElementById('btn-ai-preload');
  п?.addEventListener('click', async () => {
    showToast('📥 Прогрев моделей…', 'info', 1500);
    try {
      const текст = await preloadAIModule();
      showToast('Прогрев: ' + текст, /прогреты/.test(текст) ? 'success' : 'warning', 8000);
    } catch (e) { showToast('Прогрев не удался: ' + e.message, 'error', 5000); }
  });
}
